import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import {
  countQuizEmployeesApi,
  createQuizCampaignApi,
  listQuizSupervisorsApi,
  listQuizzesApi,
  type QuizAudience,
  type QuizListItem,
  type QuizSupervisor,
} from "../quizApi";
import { QUIZ_CAMPAIGN_PAGE, QUIZ_HUB_PAGE } from "../routes";
import "./Quiz.css";

const AUDIENCES: { value: QuizAudience; label: string }[] = [
  { value: "ALL_SUPERVISORS", label: "Todos os supervisores" },
  { value: "SELECTED", label: "Escolher" },
  { value: "ALL_EMPLOYEES", label: "Colaboradores" },
];

const QuizCampaignNewPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(QUIZ_HUB_PAGE());
  const [params] = useSearchParams();

  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [supervisors, setSupervisors] = useState<QuizSupervisor[]>([]);
  const [employeeCount, setEmployeeCount] = useState<number | null>(null);
  const [quizId, setQuizId] = useState(params.get("quizId") ?? "");
  const [title, setTitle] = useState("");
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [audience, setAudience] = useState<QuizAudience>("ALL_SUPERVISORS");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [passingScore, setPassingScore] = useState("");
  const [showAnswers, setShowAnswers] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [q, s, e] = await Promise.all([listQuizzesApi(), listQuizSupervisorsApi(), countQuizEmployeesApi()]);
        setQuizzes(q.filter((x) => x.questionCount > 0));
        setSupervisors(s);
        setEmployeeCount(e.count);
      } catch (e: any) {
        toast.error(e?.message);
      }
    })();
  }, []);

  // O título da campanha começa com o do quiz.
  useEffect(() => {
    const quiz = quizzes.find((q) => q.id === quizId);
    if (quiz && !title) setTitle(quiz.title);
  }, [quizId, quizzes, title]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? supervisors.filter((s) => s.nomeCompleto.toLowerCase().includes(term)) : supervisors;
  }, [supervisors, search]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quizId) return toast.error("Escolha o quiz.");
    if (!closesAt) return toast.error("Informe até quando o quiz fica aberto.");
    if (audience === "SELECTED" && !selected.size) return toast.error("Escolha pelo menos um supervisor.");
    const score = passingScore.trim() === "" ? undefined : Number(passingScore);
    if (score !== undefined && (Number.isNaN(score) || score < 0 || score > 100)) {
      return toast.error("A nota mínima vai de 0 a 100.");
    }

    try {
      setSaving(true);
      const campaign = await createQuizCampaignApi({
        quizId,
        title: title.trim(),
        opensAt: opensAt ? new Date(opensAt).toISOString() : undefined,
        closesAt: new Date(closesAt).toISOString(),
        audience,
        userIds: audience === "SELECTED" ? Array.from(selected) : undefined,
        showAnswers,
        passingScore: score,
      });
      toast.success(`Quiz publicado. Copie o link e mande para os ${audience === "ALL_EMPLOYEES" ? "colaboradores" : "supervisores"}.`);
      navigate(QUIZ_CAMPAIGN_PAGE(campaign.id), { replace: true });
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="quizPage">
      <main className="quizPage__main quizPage__main--narrow">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Quiz", onClick: () => navigate(QUIZ_HUB_PAGE()) },
            { label: "Publicar" },
          ]}
          title="Publicar quiz"
          subtitle="Gera um link para mandar a quem vai responder. Ele funciona até o prazo; depois disso não aceita mais respostas."
          onBack={goBack}
          backLabel="Voltar para Quiz"
        />

        <form className="quizEditor" onSubmit={handleSubmit}>
          <section className="ecoCard quizPanel">
            <div className="ecoField">
              <label htmlFor="c-quiz">Quiz</label>
              <select id="c-quiz" className="ecoSelect" value={quizId} onChange={(e) => setQuizId(e.target.value)}>
                <option value="">Selecione o quiz</option>
                {quizzes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.title} ({q.questionCount} perguntas)
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="c-title">Nome da campanha</label>
              <input
                id="c-title"
                className="ecoInput"
                maxLength={160}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Segurança — outubro"
              />
            </div>
            <div className="quizQuestion__row">
              <div className="ecoField">
                <label htmlFor="c-opens">
                  Abre em <span className="ecoLabel__optional">(vazio = agora)</span>
                </label>
                <input id="c-opens" className="ecoInput" type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} />
              </div>
              <div className="ecoField">
                <label htmlFor="c-closes">Válido até</label>
                <input
                  id="c-closes"
                  className="ecoInput"
                  type="datetime-local"
                  value={closesAt}
                  onChange={(e) => setClosesAt(e.target.value)}
                  required
                />
              </div>
            </div>
            <p className="quizHint">Data e hora de Brasília. Ex.: 21/10 às 13:00 — às 13:00 o link deixa de aceitar respostas.</p>
          </section>

          <section className="ecoCard quizPanel">
            <div>
              <h2 className="ecoCard__title">Quem responde</h2>
              <p className="ecoCard__subtitle">
                Quem responde entra pelo link digitando o CPF cadastrado no sistema. Em Colaboradores vale o CPF do cadastro do DP.
              </p>
            </div>
            <EcoSegmented label="Público" options={AUDIENCES} value={audience} onChange={setAudience} />
            {audience === "ALL_EMPLOYEES" ? (
              <p className="quizHint">
                {employeeCount ?? "…"} colaboradores do DP (não desligados, com CPF) entram hoje na campanha.
              </p>
            ) : audience === "ALL_SUPERVISORS" ? (
              <p className="quizHint">{supervisors.length} supervisores cadastrados hoje entram na campanha.</p>
            ) : (
              <div className="quizPicker">
                <input
                  className="ecoInput"
                  placeholder="Buscar supervisor"
                  aria-label="Buscar supervisor"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="quizHint">{selected.size} selecionados</span>
                <ul className="quizPicker__list">
                  {filtered.map((s) => (
                    <li key={s.id}>
                      <label className="quizPicker__item">
                        <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                        <span>{s.nomeCompleto}</span>
                        <span className="quizCard__meta">{s.userType === "SUPERVISOR_DE_CAMPO" ? "De campo" : "Supervisor"}</span>
                      </label>
                    </li>
                  ))}
                  {!filtered.length ? <li className="ecoEmpty">Nenhum supervisor encontrado.</li> : null}
                </ul>
              </div>
            )}
          </section>

          <section className="ecoCard quizPanel">
            <h2 className="ecoCard__title">Resultado</h2>
            <div className="ecoField quizQuestion__weight">
              <label htmlFor="c-pass">
                Nota mínima (%) <span className="ecoLabel__optional">(opcional)</span>
              </label>
              <input
                id="c-pass"
                className="ecoInput"
                type="number"
                min={0}
                max={100}
                value={passingScore}
                onChange={(e) => setPassingScore(e.target.value)}
              />
            </div>
            <label className="quizCheck">
              <input type="checkbox" checked={showAnswers} onChange={(e) => setShowAnswers(e.target.checked)} />
              <span>Mostrar o gabarito depois do envio</span>
            </label>
          </section>

          <div className="quizEditor__footer">
            <span />
            <div className="quizActions">
              <button type="button" className="ecoBtn" onClick={goBack} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
                {saving ? "Publicando..." : "Publicar"}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default QuizCampaignNewPage;
